import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import lanPort from "../../../assets/images/lan-port.png";
import noSignal from "../../../assets/images/no-signal.svg";
import dotSignal from "../../../assets/images/dot-signal.svg";
import oneSignal from "../../../assets/images/one-signal.svg";
import twoSignal from "../../../assets/images/two-signal.svg";
import fullSignal from "../../../assets/images/full-signal.svg";
import allApi from "../../../api/allApi";
import Loader from "../common/Loader";
import NmsTableSearch, { searchRows, sortRows } from "../common/NmsTableSearch";
import { useLocalization } from "../localization/LocalizationContext";
import { useNetwork } from "../common/networkContext";

const PAGE_SIZE = 10;
const CLIENT_FIELDS = [
  "deviceName",
  "clientType",
  "connDeviceName",
  "connDeviceModel",
  "ssid",
  "port",
  "platform",
  "macAddress",
  "ipAddress",
  "rssi",
];

const rssiIcon = (rssi) => {
  if (rssi >= 80) return fullSignal;
  if (rssi >= 60) return twoSignal;
  if (rssi >= 40) return oneSignal;
  if (rssi >= 20) return dotSignal;
  return noSignal;
};

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const isWired = (client) =>
  String(client?.clientType || "").toLowerCase() === "wired";

const clientKey = (client, index) =>
  String(client?.macAddress || client?.clientId || client?.deviceName || index);

const withType = (client, fallback) => ({
  ...client,
  clientType: client?.clientType || fallback,
});

function Client() {
  const location = useLocation();
  const navigate = useNavigate();
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [license] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("license")) || ""
  );
  const showDisconnected = license !== "Standard";

  const [wiredClients, setWiredClients] = useState([]);
  const [wirelessClients, setWirelessClients] = useState([]);
  const [disconnectedClients, setDisconnectedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [opening, setOpening] = useState("");
  const [tab, setTab] = useState("connected");
  const [filter, setFilter] = useState("all");
  const [tableQuery, setTableQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    const next = location?.state?.filter;
    if (next === "wired" || next === "wireless" || next === "all") {
      setFilter(next);
      setTab("connected");
    }
  }, [location.state]);

  useEffect(() => {
    if (!showDisconnected && tab === "disconnected") setTab("connected");
  }, [showDisconnected, tab]);

  const fetchData = async (id = networkId, { silent = false } = {}) => {
    if (!id) return;
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const api = new allApi();
      const connected = await api
        .getRequest(`api/client/connected?networkId=${id}`)
        .catch(() => ({}));
      setWiredClients(asList(connected?.wiredClients).map((client) => withType(client, "wired")));
      setWirelessClients(
        asList(connected?.wirelessClients).map((client) => withType(client, "wireless"))
      );

      if (showDisconnected) {
        const disconnected = await api
          .getRequest(`api/client/disconnected?networkId=${id}`)
          .catch(() => []);
        setDisconnectedClients(
          asList(disconnected).map((client) =>
            withType(client, isWired(client) ? "wired" : "wireless")
          )
        );
      } else {
        setDisconnectedClients([]);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      setWiredClients([]);
      setWirelessClients([]);
      setDisconnectedClients([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, showDisconnected]);

  const sourceRows = useMemo(() => {
    const pool =
      tab === "disconnected"
        ? disconnectedClients
        : filter === "wired"
          ? wiredClients
          : filter === "wireless"
            ? wirelessClients
            : [...wiredClients, ...wirelessClients];
    if (tab === "disconnected" && filter !== "all") {
      return pool.filter((client) =>
        filter === "wired" ? isWired(client) : !isWired(client)
      );
    }
    return pool;
  }, [tab, filter, wiredClients, wirelessClients, disconnectedClients]);

  const visibleRows = useMemo(
    () =>
      sortRows(
        searchRows(sourceRows, tableQuery, CLIENT_FIELDS),
        sortConfig.key,
        sortConfig.direction
      ),
    [sourceRows, tableQuery, sortConfig]
  );

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [tab, filter, tableQuery, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    setSelectedKey((current) => {
      if (current && visibleRows.some((client, index) => clientKey(client, index) === current)) {
        return current;
      }
      const first = visibleRows[0];
      return first ? clientKey(first, 0) : "";
    });
  }, [visibleRows]);

  const selectedClient =
    visibleRows.find((client, index) => clientKey(client, index) === selectedKey) ||
    null;

  const disconnectedByType = useMemo(
    () => ({
      wired: disconnectedClients.filter(isWired).length,
      wireless: disconnectedClients.filter((client) => !isWired(client)).length,
    }),
    [disconnectedClients]
  );

  const counts =
    tab === "disconnected"
      ? {
          all: disconnectedClients.length,
          wired: disconnectedByType.wired,
          wireless: disconnectedByType.wireless,
        }
      : {
          all: wiredClients.length + wirelessClients.length,
          wired: wiredClients.length,
          wireless: wirelessClients.length,
        };

  const openRoaming = async (client, event) => {
    event?.stopPropagation();
    if (!client || isWired(client) || !client.deviceName) return;
    const key = clientKey(client, client.deviceName);
    setOpening(key);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/client/clientTrends?clientName=${encodeURIComponent(
          client.deviceName
        )}&duration=48hr`
      );
      const trends =
        response?.status === "200" && Array.isArray(response?.data)
          ? response.data
          : [];
      navigate("/client-roaming", {
        state: {
          selectedClient: client,
          clientTrends: trends,
          fromPage: "clients",
        },
      });
    } catch (error) {
      console.error("Error fetching client trends:", error);
      navigate("/client-roaming", {
        state: {
          selectedClient: client,
          clientTrends: [],
          fromPage: "clients",
        },
      });
    } finally {
      setOpening("");
    }
  };

  const toggleSort = (field) => {
    setSortConfig((prev) => ({
      key: field,
      direction: prev.key === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortHeader = (field, label) => (
    <th
      className={`inc-th-sort${sortConfig.key === field ? " is-active" : ""}`}
      onClick={() => toggleSort(field)}
    >
      {label}
      <i className="fa fa-sort" aria-hidden="true" />
    </th>
  );

  const pageWindow = () => {
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    const from = Math.max(1, end - 4);
    return Array.from({ length: end - from + 1 }, (_, index) => from + index);
  };

  const rangeStart = visibleRows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, visibleRows.length);
  const selectedWired = selectedClient ? isWired(selectedClient) : false;

  const RoamingButton = ({ client, variant = "row" }) => {
    if (!client || isWired(client)) return null;
    const key = clientKey(client, client.deviceName);
    const busy = opening === key;
    return (
      <button
        type="button"
        className={variant === "primary" ? "text-btn primary-btn" : "client-roam-btn"}
        onClick={(event) => openRoaming(client, event)}
        disabled={busy}
      >
        {busy ? (
          <>
            <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading
          </>
        ) : (
          <>
            <i className="fa fa-exchange" aria-hidden="true" /> Roaming history
          </>
        )}
      </button>
    );
  };

  return (
    <div className="nms-dash client-page">
      <div className="ts-layout">
        <div className="inc-card ch-100">
          <div className="inc-card-title">
            <div className="ts-tabs" role="tablist">
              <button
                type="button"
                className={`ts-tab${tab === "connected" ? " is-active" : ""}`}
                onClick={() => setTab("connected")}
              >
                {localizationStrings?.connected} (
                {wiredClients.length + wirelessClients.length})
              </button>
              {showDisconnected && (
                <button
                  type="button"
                  className={`ts-tab${tab === "disconnected" ? " is-active" : ""}`}
                  onClick={() => setTab("disconnected")}
                >
                  {localizationStrings?.disconnected} ({disconnectedClients.length})
                </button>
              )}
            </div>
            <div className="inc-card-button">
              <NmsTableSearch
                value={tableQuery}
                onChange={setTableQuery}
                placeholder={localizationStrings?.search || "Search clients"}
              />
              <button
                className="icon-btn"
                title="Refresh"
                onClick={() => fetchData(networkId, { silent: true })}
              >
                <i
                  className={`fa ${refreshing ? "fa-spinner fa-spin" : "fa-refresh"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div className="inc-card-body">
            <div className="ts-chips client-type-chips">
              {[
                ["all", localizationStrings?.all || "All", counts.all],
                ["wired", localizationStrings?.wired || "Wired", counts.wired],
                [
                  "wireless",
                  localizationStrings?.wireless || "Wireless",
                  counts.wireless,
                ],
              ].map(([id, label, count]) => (
                <button
                  key={id}
                  type="button"
                  className={`ts-chip${filter === id ? " is-active" : ""}`}
                  onClick={() => setFilter(id)}
                >
                  {label} {count}
                </button>
              ))}
            </div>

            {loading ? (
              <Loader />
            ) : (
              <>
                <div className="inc-card-table">
                  <table className="full-table m-head">
                    <thead>
                      <tr>
                        {sortHeader("deviceName", localizationStrings?.clientName)}
                        {sortHeader("clientType", localizationStrings?.connection)}
                        {sortHeader(
                          "connDeviceName",
                          localizationStrings?.associatedDevice
                        )}
                        {sortHeader("ssid", "SSID / Port")}
                        {sortHeader("rssi", "RSSI")}
                        <th>Roaming</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.length ? (
                        pageRows.map((client, index) => {
                          const key = clientKey(client, index);
                          const wired = isWired(client);
                          return (
                            <tr
                              key={key}
                              className={`ts-device-row${
                                selectedKey === key ? " is-selected" : ""
                              }`}
                              onClick={() => setSelectedKey(key)}
                            >
                              <td>
                                <strong>{client.deviceName || "N/A"}</strong>
                                <div className="client-sub">
                                  {client.macAddress || "No MAC"}
                                </div>
                              </td>
                              <td>
                                <span className={`client-type ${wired ? "wired" : "wireless"}`}>
                                  <img
                                    src={wired ? lanPort : fullSignal}
                                    alt=""
                                  />
                                  {wired
                                    ? localizationStrings?.wired || "Wired"
                                    : localizationStrings?.wireless || "Wireless"}
                                </span>
                              </td>
                              <td>{client.connDeviceName || "N/A"}</td>
                              <td>
                                {wired
                                  ? client.port || "N/A"
                                  : client.ssid || "N/A"}
                              </td>
                              <td className="rssi-icon">
                                {wired ? (
                                  "—"
                                ) : (
                                  <img src={rssiIcon(client.rssi)} alt="" />
                                )}
                              </td>
                              <td>
                                {wired ? (
                                  <span className="client-roam-na">Not available</span>
                                ) : (
                                  <RoamingButton client={client} />
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6">
                            <div className="nms-empty">
                              {localizationStrings?.noData}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {visibleRows.length > 0 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      <p>
                        {localizationStrings?.showing} {rangeStart} - {rangeEnd}{" "}
                        {localizationStrings?.of} {visibleRows.length}{" "}
                        {localizationStrings?.records}
                      </p>
                    </div>
                    <div className="pagination">
                      <button
                        className="prev-btn"
                        onClick={() => setPage(1)}
                        disabled={currentPage === 1}
                      >
                        {localizationStrings?.firstPage}
                      </button>
                      <button
                        className="prev-btn"
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        {localizationStrings?.previous}
                      </button>
                      {pageWindow().map((num) => (
                        <button
                          key={num}
                          className={`page-btn${currentPage === num ? " active" : ""}`}
                          onClick={() => setPage(num)}
                        >
                          {num}
                        </button>
                      ))}
                      <button
                        className="next-btn"
                        onClick={() =>
                          setPage((prev) => Math.min(totalPages, prev + 1))
                        }
                        disabled={currentPage === totalPages}
                      >
                        {localizationStrings?.next}
                      </button>
                      <button
                        className="last-btn"
                        onClick={() => setPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        {localizationStrings?.lastPage}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="inc-card ts-console-card">
          <div className="inc-card-title">
            <h3>Client detail</h3>
          </div>
          <div className="inc-card-body">
            {!selectedClient ? (
              <div className="nms-empty">Select a client to inspect it.</div>
            ) : (
              <>
                <div className="client-detail-head">
                  <div>
                    <strong>{selectedClient.deviceName || "Unnamed client"}</strong>
                    <p>
                      {selectedWired
                        ? localizationStrings?.wired || "Wired"
                        : localizationStrings?.wireless || "Wireless"}
                      {" · "}
                      {tab === "disconnected"
                        ? localizationStrings?.disconnected || "Disconnected"
                        : localizationStrings?.connected || "Connected"}
                    </p>
                  </div>
                  <img
                    src={selectedWired ? lanPort : rssiIcon(selectedClient.rssi)}
                    alt=""
                    className="client-detail-signal"
                  />
                </div>

                <dl className="client-detail-list">
                  <div>
                    <dt>{localizationStrings?.macAddress}</dt>
                    <dd>{selectedClient.macAddress || "N/A"}</dd>
                  </div>
                  <div>
                    <dt>{localizationStrings?.ipAddress}</dt>
                    <dd>{selectedClient.ipAddress || "N/A"}</dd>
                  </div>
                  <div>
                    <dt>{localizationStrings?.associatedDevice}</dt>
                    <dd>{selectedClient.connDeviceName || "N/A"}</dd>
                  </div>
                  <div>
                    <dt>{localizationStrings?.deviceModel}</dt>
                    <dd>{selectedClient.connDeviceModel || "N/A"}</dd>
                  </div>
                  <div>
                    <dt>{selectedWired ? localizationStrings?.port || "Port" : "SSID"}</dt>
                    <dd>
                      {selectedWired
                        ? selectedClient.port || "N/A"
                        : selectedClient.ssid || "N/A"}
                    </dd>
                  </div>
                  <div>
                    <dt>OS</dt>
                    <dd>{selectedClient.platform || "N/A"}</dd>
                  </div>
                </dl>

                {selectedWired ? (
                  <div className="client-roam-panel is-wired">
                    <h4>Roaming history</h4>
                    <p>
                      Wired clients stay on a switch port, so they have no AP
                      roaming history. Choose a wireless client to view roam
                      events.
                    </p>
                  </div>
                ) : (
                  <div className="client-roam-panel">
                    <h4>Roaming history</h4>
                    <p>
                      See which access points this wireless client moved between
                      and how RSSI changed along the path.
                    </p>
                    <RoamingButton client={selectedClient} variant="primary" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Client;
